import { useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

export type ToastTone = 'success' | 'error' | 'info' | 'onboarding';

export interface ToastDefinition {
  id?: string;
  title?: string;
  description?: string;
  tone: ToastTone;
  content?: ReactNode;
  dismissible?: boolean;
  autoDismissMs?: number | null;
  onNext?: () => void;
  onDismiss?: () => void;
  showNext?: boolean;
}

export interface ToastRecord extends ToastDefinition {
  id: string;
}

const TOAST_LIMIT = 3;

let toasts: ToastRecord[] = [];

const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function clearToastTimer(id: string) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

function createToastId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function scheduleAutoDismiss(toast: ToastRecord) {
  clearToastTimer(toast.id);

  if (toast.autoDismissMs === null || toast.autoDismissMs === 0) {
    return;
  }

  const duration =
    toast.autoDismissMs ??
    (toast.tone === 'error' || toast.tone === 'onboarding'
      ? null
      : toast.tone === 'success'
        ? 6000
        : 5000);

  if (!duration) {
    return;
  }

  const timer = setTimeout(() => {
    dismissToast(toast.id);
  }, duration);
  timers.set(toast.id, timer);
}

export function pushToast(definition: ToastDefinition): string {
  const id = definition.id ?? createToastId();
  const nextToast: ToastRecord = { ...definition, id };
  const nextToasts = [nextToast, ...toasts.filter((toast) => toast.id !== id)].slice(
    0,
    TOAST_LIMIT,
  );

  for (const toast of toasts) {
    if (!nextToasts.some((candidate) => candidate.id === toast.id)) {
      clearToastTimer(toast.id);
    }
  }

  toasts = nextToasts;
  scheduleAutoDismiss(nextToast);
  emit();

  return id;
}

export function dismissToast(id: string) {
  clearToastTimer(id);
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
}

export function clearToasts() {
  for (const id of timers.keys()) {
    clearToastTimer(id);
  }
  toasts = [];
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return toasts;
}

export function useToastStore() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
